import TitrePage from "../components/charte/TitrePage";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box, Typography, Button, TextField, MenuItem, Select, FormControl,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Switch, Chip,
  FormControlLabel,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";

import { loaderOff, loaderOn, openSnackbar } from "../redux/actions";
import {
  TypeLieuCategorie, TypeLieuSousType,
  getTypeLieuCategories, getSousTypes,
  creerTypeLieuCategorie, modifierTypeLieuCategorie, supprimerTypeLieuCategorie, deplacerTypeLieuCategorie,
  creerSousType, modifierSousType, basculerSousTypeActif, messageErreur,
} from "../utilities/typeLieuService";
import { AIDE_REGLAGES } from "../aide/textesAide";

/* Couleurs de la charte (src/theme/tribeTheme.ts), mêmes clés qu'avant. */
const C = {
  card: "var(--c-surface)",
  card2: "var(--c-surface-2)",
  border: "var(--c-border)",
  accent: "#1F1E1B",
  accentLight: "#57534A",
  accentMuted: "rgba(31,30,27,0.07)",
  text: "#1F1E1B",
  textSub: "#57534A",
  textMuted: "#57534A",
  success: "#356A0F",
  warning: "#7C5600",
  error: "#A23A0D",
} as const;

const champSx = {
  "& .MuiOutlinedInput-root": {
    background: C.card2, color: C.text, borderRadius: "8px",
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.accentLight },
  },
  "& .MuiInputLabel-root": { color: C.textSub, fontSize: 13 },
};

/**
 * Administration du référentiel des types de lieu (lot 4.2).
 *
 * Cette page remplace trois listes codées en dur qui ne se parlaient pas — la
 * List.of de ReferenceController, la taxonomie du binaire Flutter et le repli
 * de create_quest — et qui n'étaient identiques que parce que quelqu'un les
 * avait recopiées.
 *
 * Deux différences avec le référentiel produits, assumées :
 *   - une catégorie peut être CIBLABLE ou non. « Autre » doit exister pour que
 *     ses POI soient comptables, sans pour autant être proposé au ciblage
 *     client : c'est un fourre-tout, le cocher vendrait une promesse vide.
 *   - un sous-type est unique DANS SA CATÉGORIE, pas globalement. « Café » sous
 *     Restauration et « Autre » sous plusieurs catégories sont légitimes.
 */
export default function TypesLieu() {
  const dispatch = useDispatch();

  const [categories, setCategories] = useState<TypeLieuCategorie[]>([]);
  const [sousTypes, setSousTypes]   = useState<TypeLieuSousType[]>([]);
  const [filtreCategorie, setFiltreCategorie] = useState<number | "toutes">("toutes");
  const [masquerInactifs, setMasquerInactifs] = useState(false);

  const [dialogCategorie, setDialogCategorie] = useState<
    { ouvert: boolean; id?: number; libelle: string; ciblable: boolean }>(
    { ouvert: false, libelle: "", ciblable: true });
  const [dialogSousType, setDialogSousType] = useState<
    { ouvert: boolean; id?: number; libelle: string; categorieId: number | "" }>(
    { ouvert: false, libelle: "", categorieId: "" });
  const [confirmation, setConfirmation] = useState<{ ouvert: boolean; categorie?: TypeLieuCategorie }>(
    { ouvert: false });

  const charger = useCallback(async () => {
    dispatch(loaderOn());
    try {
      const [cats, sous] = await Promise.all([getTypeLieuCategories(), getSousTypes()]);
      setCategories(cats);
      setSousTypes(sous);
    } catch (err: any) {
      dispatch(openSnackbar(messageErreur(err, "Impossible de charger le référentiel."), "red"));
    } finally {
      dispatch(loaderOff());
    }
  }, [dispatch]);

  useEffect(() => { charger(); }, [charger]);

  const sousTypesAffiches = useMemo(() => sousTypes.filter(s =>
    (filtreCategorie === "toutes" || s.categorie?.id === filtreCategorie) &&
    (!masquerInactifs || s.actif)
  ), [sousTypes, filtreCategorie, masquerInactifs]);

  const compteParCategorie = useMemo(() => {
    const n: Record<number, number> = {};
    sousTypes.forEach(s => { if (s.categorie) n[s.categorie.id] = (n[s.categorie.id] ?? 0) + 1; });
    return n;
  }, [sousTypes]);

  /* ── Catégories ── */

  const enregistrerCategorie = async () => {
    const libelle = dialogCategorie.libelle.trim();
    if (!libelle) return;
    try {
      if (dialogCategorie.id) {
        await modifierTypeLieuCategorie(dialogCategorie.id, { libelle, ciblable: dialogCategorie.ciblable });
      } else {
        await creerTypeLieuCategorie(libelle, dialogCategorie.ciblable);
      }
      setDialogCategorie({ ouvert: false, libelle: "", ciblable: true });
      dispatch(openSnackbar("Catégorie enregistrée.", "green"));
      charger();
    } catch (err: any) {
      dispatch(openSnackbar(messageErreur(err, "Enregistrement impossible."), "red"));
    }
  };

  /* L'ordre décide de la place dans les sélecteurs de création de quête et,
     après le lot 4.5, dans la grille de saisie de l'app. L'échange se fait
     en un seul appel, atomique côté serveur. */
  const deplacerCategorie = async (index: number, sens: -1 | 1) => {
    const courante = categories[index];
    if (!courante || !categories[index + sens]) return;
    try {
      await deplacerTypeLieuCategorie(courante.id, sens < 0 ? "haut" : "bas");
      charger();
    } catch (err: any) {
      dispatch(openSnackbar(messageErreur(err, "Réordonnancement impossible."), "red"));
    }
  };

  const changerCiblable = async (cat: TypeLieuCategorie) => {
    const action = cat.ciblable ? "retirer du ciblage client" : "rendre ciblable par les clients";
    if (!window.confirm(`« ${cat.libelle} » : ${action} ?`)) return;
    try {
      await modifierTypeLieuCategorie(cat.id, { ciblable: !cat.ciblable });
      charger();
    } catch (err: any) {
      dispatch(openSnackbar(messageErreur(err, "Changement impossible."), "red"));
    }
  };

  const confirmerSuppressionCategorie = async () => {
    const cat = confirmation.categorie;
    if (!cat) return;
    try {
      await supprimerTypeLieuCategorie(cat.id);
      dispatch(openSnackbar(`Catégorie « ${cat.libelle} » supprimée.`, "green"));
      charger();
    } catch (err: any) {
      dispatch(openSnackbar(messageErreur(err, "Suppression impossible."), "red"));
    } finally {
      setConfirmation({ ouvert: false });
    }
  };

  /* ── Sous-types ── */

  const enregistrerSousType = async () => {
    const libelle = dialogSousType.libelle.trim();
    if (!libelle || dialogSousType.categorieId === "") return;
    try {
      if (dialogSousType.id) {
        await modifierSousType(dialogSousType.id, { libelle, categorieId: Number(dialogSousType.categorieId) });
      } else {
        await creerSousType(libelle, Number(dialogSousType.categorieId));
      }
      setDialogSousType({ ouvert: false, libelle: "", categorieId: "" });
      dispatch(openSnackbar("Sous-type enregistré.", "green"));
      charger();
    } catch (err: any) {
      dispatch(openSnackbar(messageErreur(err, "Enregistrement impossible."), "red"));
    }
  };

  const changerActif = async (s: TypeLieuSousType) => {
    if (!window.confirm(`${s.actif ? "Désactiver" : "Réactiver"} le sous-type « ${s.libelle} » ?`)) return;
    try {
      await basculerSousTypeActif(s.id, !s.actif);
      charger();
    } catch (err: any) {
      dispatch(openSnackbar(messageErreur(err, "Changement impossible."), "red"));
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3.5 } }}>
      <TitrePage domaine="reglages" aide={AIDE_REGLAGES.typesLieu}>Types de lieu</TitrePage>
      <Typography sx={{ fontSize: 13, color: C.textSub, mb: 3 }}>
        Source unique de la taxonomie des lieux : le ciblage client, l'écran de saisie
        de l'app et les comptages lisent cette table. Un sous-type ne se supprime
        pas, il se désactive — les lieux déjà saisis gardent leur valeur.
      </Typography>

      {/* ── Catégories ── */}
      <Box sx={{ background: C.card, border: "1.5px solid #1F1E1B", borderRadius: "8px", p: 2.5, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.text }}>
            Catégories <span style={{ color: C.textMuted, fontWeight: 400 }}>({categories.length})</span>
          </Typography>
          <Button
            size="small" startIcon={<AddRoundedIcon />}
            onClick={() => setDialogCategorie({ ouvert: true, libelle: "", ciblable: true })}
            sx={{ textTransform: "none", color: C.accentLight }}
          >
            Nouvelle catégorie
          </Button>
        </Box>
        <Typography sx={{ fontSize: 11, color: C.textMuted, mb: 1.5 }}>
          L'ordre ci-dessous est celui des listes de création de quête. « Ciblable »
          décide de la présence dans le sélecteur client : une catégorie fourre-tout
          reste comptable sans être proposée.
        </Typography>

        {categories.map((cat, i) => (
          <Box key={cat.id} sx={{
            display: "flex", alignItems: "center", gap: 1,
            py: 0.75, borderBottom: i < categories.length - 1 ? `1px solid ${C.border}` : "none",
          }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <IconButton size="small" disabled={i === 0}
                onClick={() => deplacerCategorie(i, -1)} sx={{ p: 0, color: C.textMuted }}>
                <ArrowUpwardRoundedIcon sx={{ fontSize: 15 }} />
              </IconButton>
              <IconButton size="small" disabled={i === categories.length - 1}
                onClick={() => deplacerCategorie(i, 1)} sx={{ p: 0, color: C.textMuted }}>
                <ArrowDownwardRoundedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>
            <Typography sx={{ flex: 1, fontSize: 13, color: C.text }}>{cat.libelle}</Typography>
            <Chip size="small" label={`${compteParCategorie[cat.id] ?? 0} sous-type(s)`}
              sx={{ background: C.accentMuted, color: C.accentLight, fontSize: 11, height: 22 }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 108 }}>
              <Switch size="small" checked={!!cat.ciblable} onChange={() => changerCiblable(cat)} />
              <Typography sx={{ fontSize: 11, color: cat.ciblable ? C.textSub : C.textMuted }}>
                {cat.ciblable ? "Ciblable" : "Non ciblable"}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setDialogCategorie({
              ouvert: true, id: cat.id, libelle: cat.libelle, ciblable: !!cat.ciblable })}>
              <EditRoundedIcon sx={{ fontSize: 16, color: C.textSub }} />
            </IconButton>
            <IconButton size="small" onClick={() => setConfirmation({ ouvert: true, categorie: cat })}>
              <DeleteOutlineRoundedIcon sx={{ fontSize: 16, color: C.error }} />
            </IconButton>
          </Box>
        ))}
      </Box>

      {/* ── Sous-types ── */}
      <Box sx={{ background: C.card, border: "1.5px solid #1F1E1B", borderRadius: "8px", p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.text, flex: 1 }}>
            Sous-types <span style={{ color: C.textMuted, fontWeight: 400 }}>({sousTypesAffiches.length})</span>
          </Typography>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <Select value={filtreCategorie} sx={{ ...champSx["& .MuiOutlinedInput-root"], fontSize: 13 }}
              onChange={e => setFiltreCategorie(e.target.value === "toutes" ? "toutes" : Number(e.target.value))}>
              <MenuItem value="toutes">Toutes les catégories</MenuItem>
              {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.libelle}</MenuItem>)}
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Switch size="small" checked={masquerInactifs} onChange={e => setMasquerInactifs(e.target.checked)} />
            <Typography sx={{ fontSize: 12, color: C.textSub }}>Actifs seulement</Typography>
          </Box>

          <Button size="small" startIcon={<AddRoundedIcon />}
            onClick={() => setDialogSousType({ ouvert: true, libelle: "", categorieId: categories[0]?.id ?? "" })}
            sx={{ textTransform: "none", color: C.accentLight }}>
            Nouveau sous-type
          </Button>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Sous-type", "Catégorie", "Statut", ""].map((t, i) => (
                  <TableCell key={i} sx={{ color: C.textMuted, borderColor: C.border, fontSize: 11,
                    textTransform: "uppercase", letterSpacing: "0.08em" }}>{t}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sousTypesAffiches.map(s => (
                <TableRow key={s.id}>
                  <TableCell sx={{ color: s.actif ? C.text : C.textMuted, borderColor: C.border, fontSize: 13 }}>
                    {s.libelle}
                  </TableCell>
                  <TableCell sx={{ color: C.textSub, borderColor: C.border, fontSize: 12 }}>
                    {s.categorie?.libelle ?? "—"}
                  </TableCell>
                  <TableCell sx={{ borderColor: C.border }}>
                    <Chip size="small" label={s.actif ? "Actif" : "Désactivé"}
                      sx={{
                        height: 22, fontSize: 11, fontWeight: 600,
                        background: s.actif ? "rgba(53,106,15,0.12)" : "rgba(87,83,74,0.15)",
                        color: s.actif ? C.success : C.textMuted,
                      }} />
                  </TableCell>
                  <TableCell align="right" sx={{ borderColor: C.border }}>
                    <Button size="small" onClick={() => changerActif(s)}
                      sx={{ textTransform: "none", fontSize: 12, color: s.actif ? C.warning : C.success }}>
                      {s.actif ? "Désactiver" : "Réactiver"}
                    </Button>
                    <IconButton size="small" onClick={() => setDialogSousType({
                      ouvert: true, id: s.id, libelle: s.libelle, categorieId: s.categorie?.id ?? "" })}>
                      <EditRoundedIcon sx={{ fontSize: 16, color: C.textSub }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {sousTypesAffiches.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ color: C.textMuted, borderColor: C.border, fontSize: 13, py: 3, textAlign: "center" }}>
                    Aucun sous-type pour ce filtre.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ── Dialogue catégorie ── */}
      <Dialog open={dialogCategorie.ouvert}
        onClose={() => setDialogCategorie({ ouvert: false, libelle: "", ciblable: true })}
        PaperProps={{ sx: { background: C.card, border: "1.5px solid #1F1E1B", borderRadius: "8px", minWidth: 380 } }}>
        <DialogTitle sx={{ color: C.text, fontSize: 15, fontWeight: 700 }}>
          {dialogCategorie.id ? "Modifier la catégorie" : "Nouvelle catégorie"}
        </DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" label="Libellé" sx={{ ...champSx, mt: 1 }}
            value={dialogCategorie.libelle}
            onChange={e => setDialogCategorie(d => ({ ...d, libelle: e.target.value }))} />
          <FormControlLabel
            sx={{ mt: 1, color: C.textSub, "& .MuiFormControlLabel-label": { fontSize: 13 } }}
            control={<Switch size="small" checked={dialogCategorie.ciblable}
              onChange={e => setDialogCategorie(d => ({ ...d, ciblable: e.target.checked }))} />}
            label="Proposable au ciblage client" />
          <Typography sx={{ fontSize: 11, color: C.textMuted, mt: 1 }}>
            Les tarifs du pricing, les lieux et les quêtes désignent la catégorie par son
            identifiant : un renommage les suit. Les anciennes versions de l'app
            continuent d'envoyer l'ancien libellé jusqu'à leur mise à jour.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogCategorie({ ouvert: false, libelle: "", ciblable: true })}
            sx={{ textTransform: "none", color: C.textSub }}>Annuler</Button>
          <Button onClick={enregistrerCategorie} variant="contained"
            sx={{ textTransform: "none", background: C.accent }}>Enregistrer</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialogue sous-type ── */}
      <Dialog open={dialogSousType.ouvert}
        onClose={() => setDialogSousType({ ouvert: false, libelle: "", categorieId: "" })}
        PaperProps={{ sx: { background: C.card, border: "1.5px solid #1F1E1B", borderRadius: "8px", minWidth: 380 } }}>
        <DialogTitle sx={{ color: C.text, fontSize: 15, fontWeight: 700 }}>
          {dialogSousType.id ? "Modifier le sous-type" : "Nouveau sous-type"}
        </DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" label="Libellé" sx={{ ...champSx, mt: 1, mb: 2 }}
            value={dialogSousType.libelle}
            onChange={e => setDialogSousType(d => ({ ...d, libelle: e.target.value }))} />
          <FormControl fullWidth size="small">
            <Select value={dialogSousType.categorieId} displayEmpty
              sx={{ ...champSx["& .MuiOutlinedInput-root"], fontSize: 13 }}
              onChange={e => setDialogSousType(d => ({ ...d, categorieId: Number(e.target.value) }))}>
              <MenuItem value="" disabled>Choisir une catégorie</MenuItem>
              {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.libelle}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography sx={{ fontSize: 11, color: C.textMuted, mt: 1.5 }}>
            Un libellé déjà pris dans la même catégorie est refusé. Le même
            libellé sous deux catégories différentes est permis.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogSousType({ ouvert: false, libelle: "", categorieId: "" })}
            sx={{ textTransform: "none", color: C.textSub }}>Annuler</Button>
          <Button onClick={enregistrerSousType} variant="contained"
            sx={{ textTransform: "none", background: C.accent }}>Enregistrer</Button>
        </DialogActions>
      </Dialog>

      {/* ── Confirmation de suppression de catégorie ── */}
      <Dialog open={confirmation.ouvert} onClose={() => setConfirmation({ ouvert: false })}
        PaperProps={{ sx: { background: C.card, border: "1.5px solid #1F1E1B", borderRadius: "8px" } }}>
        <DialogTitle sx={{ color: C.text, fontSize: 15, fontWeight: 700 }}>Supprimer la catégorie ?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: C.textSub }}>
            « {confirmation.categorie?.libelle} » sera supprimée. L'opération est
            refusée si elle contient encore des sous-types.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmation({ ouvert: false })}
            sx={{ textTransform: "none", color: C.textSub }}>Annuler</Button>
          <Button onClick={confirmerSuppressionCategorie} variant="contained"
            sx={{ textTransform: "none", background: C.error }}>Supprimer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
